import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Container, SectionHeading, Eyebrow } from "@/components/common/Section";
import Reveal from "@/components/common/Reveal";
import CtaBand from "@/components/common/CtaBand";
import { fetchFaq } from "@/lib/apiClient";

export default function Faq() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFaq()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="faq-page">
      <section className="relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none absolute inset-0 opacity-60" />
        <Container className="relative py-20 md:py-24">
          <Reveal>
            <Eyebrow>Frequently asked questions</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-5 max-w-2xl text-4xl font-semibold leading-[1.08] tracking-[-0.02em] sm:text-5xl">
              Answers about Sentira & Licara
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Everything you need to know about the platform, security, and
              getting started. Can't find an answer?{" "}
              <Link to="/contact" className="text-primary hover:underline">
                Contact our team
              </Link>
              .
            </p>
          </Reveal>
        </Container>
      </section>

      <section className="pb-16 md:pb-24">
        <Container>
          <div className="mx-auto max-w-3xl">
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-xl bg-secondary" />
                ))}
              </div>
            ) : (
              <Accordion
                type="single"
                collapsible
                className="w-full space-y-3"
                data-testid="faq-accordion"
              >
                {items.map((item, i) => (
                  <AccordionItem
                    key={item.id}
                    value={item.id}
                    data-testid={`faq-item-${item.id}`}
                    className="rounded-[var(--radius-card)] border border-border bg-card px-5"
                  >
                    <AccordionTrigger className="py-5 text-left text-base font-medium hover:no-underline">
                      <span className="flex items-start gap-3">
                        <HelpCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                        {item.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 pl-8 text-sm leading-relaxed text-muted-foreground">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </Container>
      </section>

      <CtaBand
        title="Still have questions?"
        description="Our team is ready to walk you through Licara and answer anything specific to your organization."
        primaryLabel="Request a demo"
        secondaryLabel="Contact us"
      />
    </div>
  );
}
